import React, { useState, useCallback } from "react";
import PrimaryButton from "../components/PrimaryButton";
import toast from "react-hot-toast";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { MobileResponsive } from "./MobileResponsive";
import FloatingInput from "../components/FloatingInput";
import { emailRegex, passwordRegex } from "../utils/validation"; // Assuming you have validation here
import DynamicMetaTags from "../components/DynamicMetaTags";

const ForgotPassword = () => {
  const navigate = useNavigate();

  // Grouped state
  const [formData, setFormData] = useState({
    email: "",
    newPassword: "",
    confirmPassword: "",
    otp: "",
  });

  const [errors, setErrors] = useState({
    email: "",
    newPassword: "",
    confirmPassword: "",
    otp: "",
  });

  const [token, setToken] = useState("");
  const [screenState, setScreenState] = useState({
    showOTPScreen: false,
    showSetPassScreen: false,
  });

  // Handle input change efficiently
  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  }, []);

  // Handle email validation
  const handleEmailBlur = useCallback(() => {
    const { email } = formData;
    if (email === "") {
      setErrors((prevErrors) => ({ ...prevErrors, email: "" }));
    } else if (!emailRegex.test(email)) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        email: "Invalid email address",
      }));
    } else {
      setErrors((prevErrors) => ({ ...prevErrors, email: "" }));
    }
  }, [formData.email]);

  // Handle password validation
  const handlePasswordBlur = useCallback(
    (type) => {
      const { newPassword, confirmPassword } = formData;
      if (type === "newPassword") {
        if (!passwordRegex.test(newPassword)) {
          setErrors((prevErrors) => ({
            ...prevErrors,
            newPassword: "Password should be at least 8 characters long",
          }));
        } else {
          setErrors((prevErrors) => ({ ...prevErrors, newPassword: "" }));
        }
      } else if (type === "confirmPassword") {
        if (confirmPassword !== newPassword) {
          setErrors((prevErrors) => ({
            ...prevErrors,
            confirmPassword: "Passwords do not match",
          }));
        } else {
          setErrors((prevErrors) => ({ ...prevErrors, confirmPassword: "" }));
        }
      }
    },
    [formData.newPassword, formData.confirmPassword]
  );

  // Handle OTP validation
  const handleOtpBlur = useCallback(() => {
    const { otp } = formData;
    if (!otp || otp.length !== 5) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        otp: "OTP should be 5 digits long",
      }));
    } else {
      setErrors((prevErrors) => ({ ...prevErrors, otp: "" }));
    }
  }, [formData.otp]);

  // Submit email for OTP
  const handleForgotPass = async (e) => {
    e.preventDefault();
    if (errors.email) return;

    try {
      const res = await axios.post(
        process.env.REACT_APP_API_BASE_URL + "/admin/generate-new-otp",
        {
          email: formData.email.toLowerCase(),
        }
      );

      if ([200, 201, 202].includes(res?.status)) {
        toast.success("OTP Sent Successfully!");
        setScreenState((prev) => ({ ...prev, showOTPScreen: true }));
      }
    } catch (error) {
      // toast.error(error?.response?.data?.message);
      setErrors((prevErrors) => ({
        ...prevErrors,
        email: error?.response?.data?.message,
      }));
    }
  };

  // Submit OTP for verification
  const handleOTP = async (e) => {
    e.preventDefault();
    if (errors.otp) return;

    try {
      const res = await axios.post(
        process.env.REACT_APP_API_BASE_URL + "/admin/verify-otp",
        {
          email: formData.email.toLowerCase(),
          otp: formData.otp,
        }
      );

      if ([200, 201, 202].includes(res?.status)) {
        setToken(res?.data?.token);
        toast.success("OTP verified successfully!");
        setScreenState((prev) => ({ ...prev, showSetPassScreen: true }));
      }
    } catch (error) {
      // toast.error(error?.response?.data?.message);
      setErrors((prevErrors) => ({
        ...prevErrors,
        otp: error?.response?.data?.message,
      }));
    }
  };

  // Submit new password
  const handleSetPass = async (e) => {
    e.preventDefault();
    if (errors.newPassword || errors.confirmPassword) return;

    try {
      const res = await axios.post(
        process.env.REACT_APP_API_BASE_URL + "/admin/forgot-password",
        {
          token: token,
          newPassword: formData.newPassword,
          confirmPassword: formData.confirmPassword,
        }
      );

      if ([200, 201, 202].includes(res?.status)) {
        toast.success("Password Changed Successfully!");
        navigate("/login");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message);
      setErrors((prevErrors) => ({
        ...prevErrors,
        newPassword: error?.response?.data?.message,
        confirmPassword: error?.response?.data?.message,
      }));
    }
  };

  return (
    <>
      <DynamicMetaTags
        pageTitle="Beep - Reset Your Password for the Logistics Dashboard"
        pageDescription="Get real-time insights with Beep's dashboard! Monitor active drivers, food outlets, hubs, and detailed analytics on orders. Track Pending, Delivering, and Done orders seamlessly across outlets and hubs."
        pageKeywords="Beep, logistics dashboard, real-time analytics, active drivers, active food outlets, active hubs, order tracking, pending orders, delivering orders, completed orders, logistics insights"
      />
      <MobileResponsive />
      <div className="relative lg:flex hidden items-center justify-center h-screen">
        <div>
          <div className="flex flex-col items-center justify-center gap-5 absolute top-7 left-[4.7vw]">
            <img src="/assets/logo.svg" className="w-32" alt="" />
          </div>
          <div className="pt-7 pb-5 text-center">
            <h1 className="text-[30px] font-bold tracking-wide text-black">
              {!screenState.showOTPScreen
                ? "Forgot Password"
                : !screenState.showSetPassScreen
                ? "Enter OTP"
                : "Set New Password"}
            </h1>
            <p className="text-[#4C4C4C] text-lg my-3 font-normal tracking-widest">
              {!screenState.showOTPScreen
                ? "Enter your email to reset your admin password"
                : !screenState.showSetPassScreen
                ? "Enter the OTP sent to your email"
                : "Create a new password, confirm it, and you're all set!"}
            </p>
          </div>

          {!screenState.showOTPScreen ? (
            <form
              onSubmit={handleForgotPass}
              className="flex flex-col gap-6 w-[24vw] m-auto"
            >
              <FloatingInput
                name="email"
                id="email"
                type="email"
                placeholder="Enter Email"
                value={formData.email}
                error={errors.email}
                handleBlur={handleEmailBlur}
                onChange={handleInputChange}
              />
              <div className="flex justify-center items-center py-5">
                <PrimaryButton
                  text="Send OTP"
                  type="submit"
                  width="w-[50%]"
                  uppercase={false}
                />
              </div>
            </form>
          ) : !screenState.showSetPassScreen ? (
            <form
              onSubmit={handleOTP}
              className="flex flex-col gap-6 w-[24vw] m-auto"
            >
              <FloatingInput
                name="otp"
                id="otp"
                type="number"
                placeholder="Enter OTP"
                value={formData.otp}
                error={errors.otp}
                handleBlur={handleOtpBlur}
                onChange={handleInputChange}
              />
              <div className="flex justify-center items-center py-5">
                <PrimaryButton
                  text="Confirm"
                  type="submit"
                  width="w-[50%]"
                  uppercase={false}
                />
              </div>
            </form>
          ) : (
            <form
              onSubmit={handleSetPass}
              className="flex flex-col gap-6 w-[24vw] m-auto"
            >
              <FloatingInput
                name="newPassword"
                id="newPassword"
                type="password"
                placeholder="New Password"
                value={formData.newPassword}
                error={errors.newPassword}
                handleBlur={() => handlePasswordBlur("newPassword")}
                onChange={handleInputChange}
              />
              <FloatingInput
                name="confirmPassword"
                id="confirmPassword"
                type="password"
                placeholder="Confirm Password"
                value={formData.confirmPassword}
                error={errors.confirmPassword}
                handleBlur={() => handlePasswordBlur("confirmPassword")}
                onChange={handleInputChange}
              />
              <div className="flex justify-center items-center py-5">
                <PrimaryButton
                  text="Confirm"
                  type="submit"
                  width="w-[50%]"
                  uppercase={false}
                />
              </div>
            </form>
          )}
        </div>
        <img
          src="/assets/illus1.svg"
          className="w-full absolute bottom-0 -z-20"
          alt=""
        />
      </div>
    </>
  );
};

export default ForgotPassword;
